import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pen, BookOpen, Info, Eye, RefreshCw } from 'lucide-react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import apiClient from '../../../utils/apiClient';

export default function TrainingHub() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [maturity, setMaturity] = useState({ style: null, knowledge: null });
  const [loading, setLoading] = useState(true);
  const [styleProfile, setStyleProfile] = useState(null);
  const [showProfilePreview, setShowProfilePreview] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    fetchMaturity();
  }, []);

  const fetchMaturity = async () => {
    try {
      const response = await apiClient.get('/training/maturity');
      setMaturity(response.data);
    } catch (error) {
      console.error('Failed to fetch maturity:', error);
      // Silently fail - maturity is optional display data
    } finally {
      setLoading(false);
    }
  };

  const fetchStyleProfile = async () => {
    setLoadingProfile(true);
    try {
      const response = await apiClient.get('/training/style-profile');
      setStyleProfile(response.data);
      setShowProfilePreview(true);
    } catch (error) {
      console.error('Failed to fetch style profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isSubPage = location.pathname !== '/personal/twin/train';

  if (isSubPage) {
    return <Outlet />;
  }

  const getLevelColor = (level) => {
    const colors = {
      'None': 'bg-gray-100 text-gray-700',
      'Early': 'bg-yellow-100 text-yellow-700',
      'Developing': 'bg-blue-100 text-blue-700',
      'Strong': 'bg-green-100 text-green-700',
      'Mature': 'bg-purple-100 text-purple-700'
    };
    return colors[level] || colors['None'];
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8" data-testid="training-hub">
      <div>
        <h1 className="font-heading text-3xl font-bold text-gray-900">{t('training.title')}</h1>
        <p className="text-lg text-gray-600 mt-2">
          {t('training.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Style Training Card */}
        <Card className="p-6 rounded-2xl hover:shadow-lg transition-all border-2 border-purple-100">
          <div className="mb-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-3">
              <Pen className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-gray-900">{t('training.styleTraining')}</h3>
            <p className="text-sm text-gray-600 mt-2">
              {t('training.howYouCommunicate')}
            </p>
          </div>

          {!loading && maturity.style && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">{t('training.styleMaturity')}</p>
                  <Badge className={getLevelColor(maturity.style.level)}>
                    {maturity.style.level}
                  </Badge>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-600 transition-all duration-500" 
                    style={{ width: `${maturity.style.maturity}%` }} 
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">{maturity.style.maturity}% {t('trainingHub.mature')}</p>
                  <p className="text-xs text-gray-500">{maturity.style.itemCount} {t('trainingHub.items')}</p>
                </div>
              </div>

              {/* Style Profile Status */}
              {maturity.style.profileInfo && (
                <div className="flex items-center justify-between text-xs bg-purple-50 px-3 py-2 rounded-lg">
                  <div className="flex items-center gap-1.5">
                    <RefreshCw className="w-3 h-3 text-purple-600" />
                    <span className="text-purple-700">
                      Profile updated: {formatDate(maturity.style.profileInfo.updatedAt)}
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2 text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                    onClick={fetchStyleProfile}
                    disabled={loadingProfile}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </Button>
                </div>
              )}

              <div className="text-xs text-gray-600 bg-purple-50 p-3 rounded-lg">
                <p className="font-medium mb-1">{t('trainingHub.whatToTrain')}</p>
                <ul className="space-y-1">
                  <li>• {t('trainingHub.styleItems.writingSamples')}</li>
                  <li>• {t('trainingHub.styleItems.emailsMessages')}</li>
                  <li>• {t('trainingHub.styleItems.journalsNotes')}</li>
                </ul>
              </div>

              <Link to="/personal/twin/train/style">
                <Button className="w-full bg-purple-600 hover:bg-purple-700">
                  {t('training.trainStyle')}
                </Button>
              </Link>
            </div>
          )}
        </Card>

        {/* Knowledge Training Card */}
        <Card className="p-6 rounded-2xl hover:shadow-lg transition-all border-2 border-blue-100">
          <div className="mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-gray-900">{t('training.knowledgeTraining')}</h3>
            <p className="text-sm text-gray-600 mt-2">
              {t('training.whatYouKnow')}
            </p>
          </div>

          {!loading && maturity.knowledge && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">{t('training.knowledgeMaturity')}</p>
                  <Badge className={getLevelColor(maturity.knowledge.level)}>
                    {maturity.knowledge.level}
                  </Badge>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 transition-all duration-500" 
                    style={{ width: `${maturity.knowledge.maturity}%` }} 
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">{maturity.knowledge.maturity}% {t('trainingHub.mature')}</p>
                  <p className="text-xs text-gray-500">{maturity.knowledge.itemCount} {t('trainingHub.items')}</p>
                </div>
              </div>

              <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-lg">
                <p className="font-medium mb-1">{t('trainingHub.whatToTrain')}</p>
                <ul className="space-y-1">
                  <li>• {t('trainingHub.knowledgeItems.factsInfo')}</li>
                  <li>• {t('trainingHub.knowledgeItems.docsReferences')}</li>
                  <li>• {t('trainingHub.knowledgeItems.faqsProcesses')}</li>
                </ul>
              </div>

              <Link to="/personal/twin/train/knowledge">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  {t('trainingHub.trainKnowledge')}
                </Button>
              </Link>
            </div>
          )}
        </Card>
      </div>

      {/* Info Card */}
      <Card className="p-6 rounded-2xl bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">
              Understanding Style vs Knowledge
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
              <div>
                <p className="font-medium text-purple-700 mb-1">Style Training</p>
                <p>Teaches your Twin <strong>how to communicate</strong> like you - your tone, writing patterns, and way of expressing ideas.</p>
              </div>
              <div>
                <p className="font-medium text-blue-700 mb-1">Knowledge Training</p>
                <p>Provides your Twin with <strong>information to reference</strong> - facts, documents, and data it can use to answer questions.</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Style Profile Preview Dialog */}
      <Dialog open={showProfilePreview} onOpenChange={setShowProfilePreview}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pen className="w-5 h-5 text-purple-600" />
              Your Style Profile
            </DialogTitle>
            <DialogDescription>
              This profile is extracted from your Style training data and guides how your Twin communicates.
            </DialogDescription>
          </DialogHeader>
          
          {styleProfile?.exists ? (
            <div className="space-y-4 mt-4">
              {/* Profile Attributes */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium">Tone</p>
                  <p className="text-gray-900 capitalize">{styleProfile.profile.tone}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium">Formality</p>
                  <p className="text-gray-900">
                    {styleProfile.profile.formality < 0.3 ? 'Casual' : 
                     styleProfile.profile.formality < 0.6 ? 'Balanced' : 'Formal'}
                  </p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium">Verbosity</p>
                  <p className="text-gray-900">
                    {styleProfile.profile.verbosity < 0.3 ? 'Concise' : 
                     styleProfile.profile.verbosity < 0.6 ? 'Moderate' : 'Detailed'}
                  </p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium">Avg Sentence</p>
                  <p className="text-gray-900">{styleProfile.profile.sentenceLength.avgWords} words</p>
                </div>
              </div>

              {/* Structure Preferences */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600 font-medium mb-2">Structure Preferences</p>
                <div className="flex flex-wrap gap-1.5">
                  {styleProfile.profile.structurePrefs.usesBulletsOften && (
                    <Badge variant="secondary" className="text-xs">Uses Bullets</Badge>
                  )}
                  {styleProfile.profile.structurePrefs.usesHeadingsOften && (
                    <Badge variant="secondary" className="text-xs">Uses Headings</Badge>
                  )}
                  {styleProfile.profile.structurePrefs.prefersShortParagraphs && (
                    <Badge variant="secondary" className="text-xs">Short Paragraphs</Badge>
                  )}
                  {styleProfile.profile.structurePrefs.prefersNextSteps && (
                    <Badge variant="secondary" className="text-xs">Includes Next Steps</Badge>
                  )}
                </div>
              </div>

              {/* Greeting Style */}
              {(styleProfile.profile.greetingStyle.usesGreeting || styleProfile.profile.greetingStyle.usesClosing) && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 font-medium mb-2">Greeting & Closing Style</p>
                  <div className="text-sm text-gray-700 space-y-1">
                    {styleProfile.profile.greetingStyle.usesGreeting && styleProfile.profile.greetingStyle.commonGreetings.length > 0 && (
                      <p>Greetings: {styleProfile.profile.greetingStyle.commonGreetings.join(', ')}</p>
                    )}
                    {styleProfile.profile.greetingStyle.usesClosing && styleProfile.profile.greetingStyle.commonClosings.length > 0 && (
                      <p>Closings: {styleProfile.profile.greetingStyle.commonClosings.join(', ')}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Prompt Block Preview */}
              <div className="border rounded-lg">
                <div className="bg-gray-100 px-3 py-2 border-b">
                  <p className="text-xs text-gray-600 font-medium">What Your Twin Sees (Prompt Injection)</p>
                </div>
                <pre className="p-3 text-xs text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 max-h-48 overflow-y-auto">
                  {styleProfile.promptBlock}
                </pre>
              </div>

              {/* Source Stats */}
              <div className="text-xs text-gray-500 text-center">
                Based on {styleProfile.profile.sourceStats.styleItemsCount} style items • 
                Last updated: {formatDate(styleProfile.profile.updatedAt)}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No Style Profile yet.</p>
              <p className="text-sm mt-1">Upload Style training data to create one.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
